import { GenericShortListFilter } from '@hotjar/global-filters';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';
import { SOLVED_STATUS_CONFIG } from './use-filter-state';
import type { FiltersState } from './use-filter-state';

type SolvedStatusType = FiltersState['status'];

interface SolvedStatusFilterProps {
  onApply: (value: SolvedStatusType) => void;
  selected: SolvedStatusType;
}

function mapArrayStatusToString(statuses?: SolvedStatusType[]): SolvedStatusType {
  return !statuses || statuses.length === 0 || statuses.length >= 2 ? 'both' : statuses[0];
}

function mapStringStatusToArray(status: SolvedStatusType): SolvedStatusType[] {
  return status === 'both' ? [] : [status];
}

export const SolvedStatusFilter = ({
  onApply,
  selected,
}: SolvedStatusFilterProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [currentValue, setCurrentValue] = useState(mapStringStatusToArray(selected));

  // reset the current values after route change
  useEffect(() => {
    setCurrentValue(mapStringStatusToArray(selected));
  }, [setCurrentValue, selected]);

  const options = SOLVED_STATUS_CONFIG.map((x) => ({
    value: x.value,
    label: t(x.labelKey),
  }));

  const onChange = (value?: SolvedStatusType[]) => {
    setCurrentValue(value ?? []);
    onApply(mapArrayStatusToString(value));
  };

  const onRemove = () => onChange([]);

  return (
    <GenericShortListFilter<SolvedStatusType>
      filterName={t('highlights:filters.status.filter-name')}
      label2={currentValue.length === 1 ? t(`highlights:single.${currentValue[0]}`) : 'Any'}
      iconName={isEqual(currentValue, ['solved']) ? 'check_alt' : 'check'}
      currentValue={currentValue}
      optionList={options}
      showDropdownCaret
      onApply={onChange}
      isInactive={currentValue.length === 0}
      onRemove={currentValue.length ? onRemove : undefined}
    />
  );
};
